import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Spinner } from '@/components/admin/ui';
import {
  DeviceLink,
  UserLink,
} from '@/components';
import { formatAdminDateTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type ThreadStatus = 'open' | 'archived';

type SupportMessage = {
  id: string;
  author: string;
  body: string;
  created_at: string;
};

type ThreadDetail = {
  id: string;
  status: ThreadStatus;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  user_last_read_at: string | null;
  admin_last_read_at: string | null;
  installation_id: string | null;
  account_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
  unread: boolean;
  messages: SupportMessage[];
};

export const Route = createFileRoute('/admin/support/$id')({
  component: SupportDetailPage,
});

function SupportDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'support', id],
    queryFn: () =>
      apiJson<ThreadDetail>(`/v1/admin/support/threads/${encodeURIComponent(id)}`),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages.length]);

  const send = useMutation({
    mutationFn: (message: string) =>
      apiJson<ThreadDetail>(
        `/v1/admin/support/threads/${encodeURIComponent(id)}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({ message }),
        },
      ),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['admin', 'support', id] });
      void qc.invalidateQueries({ queryKey: ['admin', 'support'] });
    },
  });

  const patch = useMutation({
    mutationFn: (body: { status: ThreadStatus }) =>
      apiJson<ThreadDetail>(`/v1/admin/support/threads/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'support', id] });
      void qc.invalidateQueries({ queryKey: ['admin', 'support'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  if (!data) return null;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Support chat</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-nova-muted-foreground">
            {data.status === 'archived' ? (
              <Badge variant="neutral">Archived</Badge>
            ) : (
              <Badge variant="primary">Open</Badge>
            )}
            {data.account_id ? (
              <UserLink
                userId={data.account_id}
                displayName={data.user_display_name}
                email={data.user_email}
              >
                {data.user_display_name ?? data.user_email ?? data.account_id}
              </UserLink>
            ) : data.installation_id ? (
              <DeviceLink installationId={data.installation_id} label="short" />
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.status === 'open' ? (
            <Button
              variant="secondary"
              loading={patch.isPending}
              onClick={() => void patch.mutateAsync({ status: 'archived' })}
            >
              Archive
            </Button>
          ) : (
            <Button
              variant="secondary"
              loading={patch.isPending}
              onClick={() => void patch.mutateAsync({ status: 'open' })}
            >
              Reopen
            </Button>
          )}
          <Button variant="secondary" onClick={() => void navigate({ to: '/admin/support' })}>
            Back
          </Button>
        </div>
      </div>

      <Card className="flex min-h-[420px] flex-col">
        <div className="flex-1 space-y-3 overflow-y-auto px-1 py-2">
          {data.messages.length === 0 ? (
            <p className="text-sm text-nova-muted-foreground">No messages yet.</p>
          ) : (
            data.messages.map((m) => {
              const isAdmin = m.author === 'admin';
              return (
                <div
                  key={m.id}
                  className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm ${
                      isAdmin
                        ? 'bg-nova-primary text-nova-primary-foreground'
                        : 'bg-nova-muted text-nova-foreground'
                    }`}
                  >
                    <div
                      className={`mb-1 text-[11px] font-medium ${
                        isAdmin ? 'text-white/80' : 'text-nova-muted-foreground'
                      }`}
                    >
                      {isAdmin ? 'Hmonglus' : 'User'} · {formatAdminDateTime(m.created_at)}
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        <form
          className="mt-4 flex flex-col gap-2 border-t border-nova-border pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = draft.trim();
            if (!trimmed || send.isPending) return;
            void send.mutateAsync(trimmed);
          }}
        >
          <textarea
            className="min-h-24 w-full rounded-lg border border-nova-input py-2 px-2.5 text-sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a reply… (user gets a push notification)"
          />
          {send.isError ? (
            <p className="text-sm text-red-600">{(send.error as Error).message}</p>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" loading={send.isPending} disabled={!draft.trim()}>
              Send reply
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
